// @ts-nocheck
import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Hidden from "@insite/mobius/Hidden";
// import Img, { ImgProps } from "@insite/mobius/Img"; // Starter Custom Code
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { headerHideForDesktopStyles, headerHideForMobileStyles } from "@store/Themes/common/mixins/header";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
const isVectorialRegExp = new RegExp(/\.svg(\?.*)?$/);
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    homePageLink: getHomePageUrl(state),
});

const enum fields {
    logoImage = "logoImage",
    isMobileSpecific = "isMobileSpecific",
    mobileSpecificImage = "mobileSpecificImage",
    customCSS = "customCSS",
    // Store Custom Code
    isRetina = "isRetina",
    mobileIsRetina = "mobileIsRetina",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoImage]: string;
        [fields.isMobileSpecific]: boolean;
        [fields.mobileSpecificImage]: string;
        [fields.customCSS]: string;
        // Store Custom Code
        [fields.isRetina]: boolean;
        [fields.mobileIsRetina]: boolean;
    };
    extendedStyles?: LogoStyles;
    externalCustomCSS?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LogoStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // Starter Custom Code
    imgMobile?: ImgProps;
}

export const logoStyles: LogoStyles = {
    wrapper: {
        css: css`
            display: flex;
            /* Store Custom Code - Start */
            @media screen {
                justify-content: center;
            }
            /* Store Custom Code - End */
        `,
    },
    imgMobile: {
        /* Store Custom Code - Start */
        css: css`
            ${headerHideForDesktopStyles()}
            @media print {
                display: none;
            }
        `,
        /* Store Custom Code - End */
    },
    /* Store Custom Code - Start */
    img: {
        css: css`
            @media print {
                display: block;
            }
        `,
    },
    /* Store Custom Code - End */
};

export const Logo: React.FunctionComponent<Props> = ({
    fields,
    extendedStyles,
    homePageLink,
    externalCustomCSS,
}: Props) => {
    const styles = useMergeStyles("logo", logoStyles, extendedStyles);
    const isEager = useEagerLogoLoading();

    /* Starter Custom Code - Start */
    const sharedProps: ImgProps = React.useMemo(
        () => ({
            loading: isEager ? "eager" : "lazy",
            altText: translate("Home"),
            hideLoading: true,
        }),
        [isEager],
    );

    const imgCss = React.useMemo(
        () => css`
            ${fields.isMobileSpecific && fields.mobileSpecificImage ? headerHideForMobileStyles() : null}
            ${styles.img?.css}
        `,
        [styles, fields.mobileSpecificImage],
    );
    /* Starter Custom Code - End */

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css /* Starter Custom Code */}
            ${externalCustomCSS || fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            {/* Starter Removed - StyledWrapper */}
            <Link href={homePageLink} className="home-page-link">
                {/* Starter Custom Code - Start */}
                {fields.isMobileSpecific && fields.mobileSpecificImage ? (
                    <Img
                        src={fields.mobileSpecificImage}
                        {...sharedProps}
                        {...styles.imgMobile}
                        // Store Custom Code
                        isRetina={fields.mobileIsRetina}
                    />
                ) : null}
                <Img
                    src={fields.logoImage}
                    {...sharedProps}
                    {...styles.img}
                    css={imgCss}
                    className="img"
                    // Store Custom Code
                    isRetina={fields.isRetina}
                />
                {/* Starter Custom Code - End */}
            </Link>
        </StyledWrapper>
    );
};

/* Starter Custom Code - Start */
const defaultCustomCss = `& {
}

.home-page-link {
}

.img {
}
`;
/* Starter Custom Code - End */

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Logo),
    definition: {
        group: "Basic",
        icon: "diamond",
        fieldDefinitions: [
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            /* Store Custom Code - Start */
            {
                name: fields.isRetina,
                displayName: "Enable Retina Scaling",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                isVisible: widget => {
                    return widget.fields.logoImage && !isVectorialRegExp.test(widget.fields.logoImage);
                },
                tab: basicTab,
            },
            /* Store Custom Code - End */
            {
                name: fields.isMobileSpecific,
                displayName: "Mobile Specific Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.mobileSpecificImage,
                displayName: "Mobile Logo",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.isMobileSpecific,
                tab: basicTab,
            },
            /* Store Custom Code - Start */
            {
                name: fields.mobileIsRetina,
                displayName: "Enable Mobile Retina Scaling",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                isVisible: widget =>
                    widget.fields.mobileSpecificImage && !isVectorialRegExp.test(widget.fields.mobileSpecificImage),
                tab: basicTab,
            },
            /* Store Custom Code - End */
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
